import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Plus, Minus, ShoppingBag, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WATER_SLUG = "bacteriostatic-water";

function getDiscountPercent(qualifiedCount) {
    if (qualifiedCount >= 10) return 30;
    if (qualifiedCount >= 5) return 20;
    if (qualifiedCount >= 3) return 10;
    return 0;
}

export default function Bundles() {
    const [products, setProducts] = useState([]);
    const [counts, setCounts] = useState({});
    const { addItem, setIsOpen } = useCart();

    useEffect(() => {
        axios.get(`${API}/products`).then((r) => setProducts(r.data));
    }, []);

    const setQty = (id, delta) => {
        setCounts((c) => {
            const next = Math.max(0, (c[id] || 0) + delta);
            return { ...c, [id]: next };
        });
    };

    const setQtyTo = (id, val) => {
        setCounts((c) => ({ ...c, [id]: Math.max(0, val) }));
    };

    const totals = useMemo(() => {
        let subtotal = 0;
        let totalQty = 0;
        let qualifiedQty = 0;
        products.forEach((p) => {
            const q = counts[p.id] || 0;
            if (q > 0) {
                subtotal += q * p.price;
                totalQty += q;
                if (p.slug !== WATER_SLUG) qualifiedQty += q;
            }
        });
        const discountPercent = getDiscountPercent(qualifiedQty);
        const discountAmount = +(subtotal * (discountPercent / 100)).toFixed(2);
        const total = +(subtotal - discountAmount).toFixed(2);
        return { subtotal, totalQty, qualifiedQty, discountPercent, discountAmount, total };
    }, [products, counts]);

    const handleAddBundle = () => {
        products.forEach((p) => {
            const q = counts[p.id] || 0;
            if (q > 0) addItem(p, q);
        });
        // Save bundle discount in localStorage for checkout
        localStorage.setItem("pepstep_bundle_discount", String(totals.discountPercent));
        setCounts({});
        setIsOpen(true);
    };

    const tiers = [
        { items: 3, pct: 10 },
        { items: 5, pct: 20 },
        { items: 10, pct: 30 },
    ];

    return (
        <div className="pt-32 pb-24 min-h-screen" data-testid="bundles-page">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="mb-12 max-w-3xl">
                    <p className="text-pep-primary text-sm uppercase tracking-widest font-medium mb-3">Bundle Builder</p>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-pep-navy font-medium">
                        Build your own bundle.
                    </h1>
                    <p className="text-pep-muted mt-4 text-lg leading-relaxed">
                        Mix any 3+ peptides and save automatically. The more you add, the more you save.
                        Bacteriostatic water doesn't count toward your discount tier (but you can still add it!).
                    </p>
                </div>

                {/* Tiers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                    {tiers.map((t, i) => (
                        <div
                            key={i}
                            data-testid={`bundle-tier-${t.items}`}
                            className={`rounded-3xl p-6 border transition-all ${
                                totals.qualifiedQty >= t.items
                                    ? "bg-pep-navy text-white border-pep-navy"
                                    : "bg-white border-pep-border/60 text-pep-navy"
                            }`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <p className={`text-xs uppercase tracking-widest ${totals.qualifiedQty >= t.items ? "text-pep-accent" : "text-pep-muted"}`}>
                                    Tier {i + 1}
                                </p>
                                {totals.qualifiedQty >= t.items && (
                                    <span className="text-[10px] bg-pep-accent text-pep-navy px-2 py-1 rounded-full font-medium">
                                        Active
                                    </span>
                                )}
                            </div>
                            <p className="font-heading text-3xl font-medium">{t.items}+ items</p>
                            <p className={`mt-1 ${totals.qualifiedQty >= t.items ? "text-pep-accent" : "text-pep-primary"} font-medium`}>
                                {t.pct}% off
                            </p>
                        </div>
                    ))}
                </div>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Product list */}
                    <div className="lg:col-span-2 space-y-3" data-testid="bundle-product-list">
                        {products.map((p) => {
                            const q = counts[p.id] || 0;
                            return (
                                <div
                                    key={p.id}
                                    data-testid={`bundle-row-${p.slug}`}
                                    className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-pep-border/60 lift"
                                >
                                    <div className="w-16 h-16 rounded-xl bg-pep-surface overflow-hidden flex-shrink-0">
                                        {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-pep-navy">{p.name}</p>
                                        <p className="text-sm text-pep-muted">
                                            ${p.price.toFixed(2)}
                                            {p.slug === WATER_SLUG && (
                                                <span className="ml-2 text-[10px] uppercase tracking-widest text-pep-primary">
                                                    Excluded from discount
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 bg-pep-surface rounded-full p-1">
                                        <button
                                            onClick={() => setQty(p.id, -1)}
                                            className="w-8 h-8 rounded-full bg-white hover:text-pep-primary flex items-center justify-center"
                                            data-testid={`bundle-decrease-${p.slug}`}
                                            aria-label={`Decrease ${p.name}`}
                                        >
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <input
                                            type="number"
                                            min="0"
                                            value={q}
                                            onChange={(e) => setQtyTo(p.id, parseInt(e.target.value) || 0)}
                                            className="w-10 text-center bg-transparent text-sm font-medium focus:outline-none"
                                            data-testid={`bundle-qty-${p.slug}`}
                                        />
                                        <button
                                            onClick={() => setQty(p.id, 1)}
                                            className="w-8 h-8 rounded-full bg-white hover:text-pep-primary flex items-center justify-center"
                                            data-testid={`bundle-increase-${p.slug}`}
                                            aria-label={`Increase ${p.name}`}
                                        >
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Summary */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 bg-pep-navy text-white rounded-3xl p-8" data-testid="bundle-summary">
                            <p className="text-xs uppercase tracking-widest text-pep-accent mb-2">Your Bundle</p>
                            <h3 className="font-heading text-3xl font-medium mb-6">
                                {totals.totalQty} {totals.totalQty === 1 ? "item" : "items"}
                            </h3>
                            <div className="space-y-3 border-t border-white/10 pt-6">
                                <div className="flex justify-between text-sm">
                                    <span className="text-white/60">Subtotal</span>
                                    <span data-testid="bundle-subtotal">${totals.subtotal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-white/60">Bundle discount ({totals.discountPercent}%)</span>
                                    <span className="text-pep-accent" data-testid="bundle-discount-amount">
                                        −${totals.discountAmount.toFixed(2)}
                                    </span>
                                </div>
                                <div className="flex justify-between font-heading text-2xl pt-3 border-t border-white/10">
                                    <span>Total</span>
                                    <span data-testid="bundle-total">${totals.total.toFixed(2)}</span>
                                </div>
                            </div>
                            {totals.discountPercent === 0 && totals.qualifiedQty > 0 && (
                                <div className="mt-4 p-3 rounded-xl bg-white/5 text-xs text-pep-accent flex items-center gap-2">
                                    <Sparkles className="w-3 h-3" />
                                    Add {3 - totals.qualifiedQty} more {3 - totals.qualifiedQty === 1 ? "item" : "items"} to unlock 10% off
                                </div>
                            )}
                            <button
                                disabled={totals.totalQty === 0}
                                onClick={handleAddBundle}
                                className="mt-6 w-full bg-pep-primary text-white rounded-full py-4 font-medium hover:bg-white hover:text-pep-navy transition disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                data-testid="bundle-add-to-cart"
                            >
                                <ShoppingBag className="w-4 h-4" /> Add Bundle to Cart
                            </button>
                            <Link to="/checkout" className="block mt-3 text-center text-xs text-white/40 hover:text-white">
                                Already have items? Go to checkout
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
